/** @jsxImportSource @emotion/react */
/**
 * Таблица посадок
 * Поддерживает группировку по культурам
 */
import React from 'react';
import { Table, Collapse, Space, Tag, Typography, type TableColumnsType } from 'antd';
import type { Planting } from '../../../models/Planting';
import PlantingLifecycleTag from '../../../features/plantings/components/PlantingLifecycleTag';
import CheckboxCell from './CheckboxCell';
import PlantingBbchTag from './PlantingBbchTag';
import { getLocationCode, getFullPhotoUrl, getPlantingDisplayQuantity } from '../utils/plantingsFormatters';
import type { CropGroup } from '../hooks/usePlantingsGrouping';
import { Thumbnail, ImagePlaceholder, MicroText } from '../../../styles/components';
import { commonStyles } from '../../../styles/commonStyles';

const { Text } = Typography;

interface PlantingsTableProps {
  plantings: Planting[];
  loading?: boolean;
  rowSelection?: any;
  onView: (planting: Planting) => void;
  groupedData?: CropGroup[]; // Данные с группировкой
}

/**
 * Генерирует колонки таблицы
 */
const createTableColumns = (): TableColumnsType<Planting> => [
  {
    title: '',
    width: 60,
    render: (_, record) => {
      const photoUrl = record.photos && record.photos.length > 0
        ? getFullPhotoUrl(record.photos[0].thumbnailUrl || record.photos[0].url)
        : null;
      
      return photoUrl ? (
        <Thumbnail
          src={photoUrl}
          alt={record.crop?.name}
          size="small"
        />
      ) : (
        <ImagePlaceholder size="small">
          <MicroText>—</MicroText>
        </ImagePlaceholder>
      );
    },
  },
  {
    title: 'Локация',
    width: 120,
    render: (_, record) => (
      <Text type="secondary" css={[commonStyles.smallText, commonStyles.monoText]}>
        {getLocationCode(record)}
      </Text>
    ),
  },
  {
    title: 'Статус',
    width: 100,
    render: (_, record) => <PlantingLifecycleTag status={record.lifecycleStatus} />,
  },
  {
    title: 'BBCH',
    width: 90,
    render: (_, record) => <PlantingBbchTag bbch={record.bbch} fallback="—" />,
  },
  {
    title: 'Кол-во',
    width: 60,
    align: 'center' as const,
    render: (_, record) => getPlantingDisplayQuantity(record),
  },
];

/**
 * Создает конфигурацию rowSelection с расширенной зоной чекбокса
 */
const createRowSelection = (rowSelection: any) => {
  if (!rowSelection) return undefined;

  return {
    ...rowSelection,
    columnWidth: 64,
    renderCell: (checked: boolean, record: Planting, index: number, originNode: React.ReactNode) => (
      <CheckboxCell
        checked={checked}
        recordId={record.id}
        selectedRowKeys={rowSelection.selectedRowKeys || []}
        onChange={rowSelection.onChange}
      >
        {originNode}
      </CheckboxCell>
    ),
  };
};

const PlantingsTable: React.FC<PlantingsTableProps> = ({
  plantings,
  loading,
  rowSelection,
  onView,
  groupedData,
}) => {
  const columns = createTableColumns();
  const enhancedRowSelection = createRowSelection(rowSelection);

  // Без группировки - простая таблица
  if (!groupedData) {
    return (
      <Table<Planting>
        rowKey={(record) => String(record.id)}
        dataSource={plantings}
        columns={columns}
        loading={loading}
        pagination={{ pageSize: 50 }}
        size="small"
        rowSelection={enhancedRowSelection}
        onRow={(record) => ({
          onClick: () => onView(record),
          style: { cursor: 'pointer' },
        })}
      />
    );
  }

  // С группировкой - Collapse с таблицами
  return (
    <Collapse
      items={groupedData.map((cropGroup) => {
        const allPlantings = cropGroup.locations.flatMap(loc => loc.items);
        
        return {
          key: cropGroup.cropKey,
          label: (
            <Space>
              <Text strong css={{ fontSize: 16 }}>
                {cropGroup.cropName}
              </Text>
              {cropGroup.cropLatinName && (
                <Text type="secondary" css={commonStyles.smallText}>
                  {cropGroup.cropLatinName}
                </Text>
              )}
              <Tag color="blue">{cropGroup.totalQuantity} шт.</Tag>
            </Space>
          ),
          children: (
            <Table<Planting>
              rowKey={(record) => String(record.id)}
              dataSource={allPlantings}
              columns={columns}
              loading={loading}
              pagination={false}
              size="small"
              rowSelection={enhancedRowSelection}
              onRow={(record) => ({
                onClick: () => onView(record),
                style: { cursor: 'pointer' },
              })}
            />
          ),
        };
      })}
    />
  );
};

export default PlantingsTable;
